import React, { useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, Grid, MenuItem, Stack, TextField,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";

const CATEGORIES = ["Laptop", "Desktop", "Monitor", "Peripheral", "Furniture", "Stationery", "Mobile", "Tablet", "Network Equipment", "Other"];
const STATUSES = ["In Stock", "Assigned", "Under Maintenance", "Retired", "Lost"];

const EMPTY_FORM = {
  boxID: "", serialNumber: "", itemName: "", category: "Laptop",
  brandModel: "", supplier: "",
  purchaseDate: "", warrantyExpiryDate: "",
  price: "", stock: 1, notes: "", status: "In Stock",
};

export default function InventoryFormNew() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const editID = searchParams.get("edit");
  const isEdit = Boolean(editID);

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  // Pre-populate form when editing an existing item
  useEffect(() => {
    if (!editID) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/inventory/getinventorydetail/${editID}`);
        const d = res.data;
        setForm({
          boxID: d.boxID || "",
          serialNumber: d.serialNumber || "",
          itemName: d.itemName || "",
          category: d.category || "Laptop",
          brandModel: d.brandModel || "",
          supplier: d.supplier || "",
          purchaseDate: d.purchaseDate ? d.purchaseDate.slice(0, 10) : "",
          warrantyExpiryDate: d.warrantyExpiryDate ? d.warrantyExpiryDate.slice(0, 10) : "",
          price: d.price ?? "",
          stock: d.stock ?? 1,
          notes: d.notes || "",
          status: d.status || "In Stock",
        });
      } catch (err) {
        toast.error(extractErrorMessage(err));
      } finally { setLoading(false); }
    })();
  }, [editID]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.itemName) return toast.error("Item name is required");
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/api/inventory/${editID}`, form);
        toast.success("Inventory item updated");
      } else {
        await api.post("/api/inventory/office-inventory", form);
        toast.success("Inventory item created");
      }
      nav("/InventoryList");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title={isEdit ? "Edit Inventory Item" : "Add Inventory Item"}
        subtitle={isEdit ? `Editing item ${editID}` : "Register new office asset or supply"}
        icon={<Inventory2OutlinedIcon />}
        actions={[<Button key="back" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => nav("/InventoryList")}>Back to Inventory</Button>]}
      />

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <SectionCard title="Item Information" accent="#6C50BF" sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Item Name *" value={form.itemName} onChange={update("itemName")} required /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth select label="Category" value={form.category} onChange={update("category")}>{sortByLabel(CATEGORIES).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Brand / Model" value={form.brandModel} onChange={update("brandModel")} placeholder="e.g. Dell XPS 13" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Supplier" value={form.supplier} onChange={update("supplier")} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Serial Number" value={form.serialNumber} onChange={update("serialNumber")} placeholder="Unique identifier" /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Box / Shelf ID" value={form.boxID} onChange={update("boxID")} placeholder="Storage location" /></Grid>
              </Grid>
            </SectionCard>

            <SectionCard title="Purchase & Warranty" accent="#F9BC06" sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Purchase Date" InputLabelProps={{ shrink: true }} value={form.purchaseDate} onChange={update("purchaseDate")} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Warranty Expiry" InputLabelProps={{ shrink: true }} value={form.warrantyExpiryDate} onChange={update("warrantyExpiryDate")} inputProps={{ min: form.purchaseDate }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Price (₹)" value={form.price} onChange={update("price")} inputProps={{ min: 0 }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Stock Quantity" value={form.stock} onChange={update("stock")} inputProps={{ min: 0 }} /></Grid>
              </Grid>
            </SectionCard>

            <SectionCard title="Notes" accent="#5C85FF">
              <TextField fullWidth multiline minRows={3} label="Additional notes" value={form.notes} onChange={update("notes")} placeholder="Condition, special instructions, accessories…" />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
              <SectionCard title="Status" accent="#39C645">
                <Stack spacing={2}>
                  <TextField fullWidth select label="Current Status" value={form.status} onChange={update("status")}>
                    {sortByLabel(STATUSES).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                  <Button
                    type="submit" variant="contained" size="large" fullWidth
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
                    disabled={loading || !form.itemName}
                  >
                    {loading ? "Saving…" : isEdit ? "Update Item" : "Save Item"}
                  </Button>
                </Stack>
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
